import { ReactNode, useMemo, useState } from "react";
import { Outlet, useParams } from "react-router-dom";

import DocumentationIconButton from "components/DocumentationIconButton";
import NotFoundPage from "components/error/NotFoundPage";
import MetaInfoList from "components/MetaInfoList";
import DesktopView from "components/Mobile/DesktopView";
import MobileView from "components/Mobile/MobileView";
import SearchInput from "components/SearchInput";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import BadgeNext from "ds/components/BadgeNext";
import useTitle from "hooks/useTitle";
import useTypedFlags from "hooks/useTypedFlags";

import { getIntegrationById, isValidIntegrationId } from "../../registry";
import { IntegrationsOutletContext } from "../../types";
import { IntegrationId } from "../../registry/constants";
import { isIntegrationNew } from "../../utils/isIntegrationNew";

const IntegrationGroupLayout = () => {
  const { intentNewLayout } = useTypedFlags();
  const { integrationGroup, integrationId } = useParams<{
    integrationGroup: string;
    integrationId?: string;
  }>();
  const [title, setTitle] = useState<string>("");
  const [heading, setHeading] = useState<ReactNode>("Integrations");

  const [layoutActions, setLayoutActions] = useState<ReactNode>(null);
  const [layoutMeta, setLayoutMeta] = useState<ReactNode>(null);
  const [layoutNavigation, setLayoutNavigation] = useState<ReactNode>(null);

  const [integration, showNewBadge, showSearch] = useMemo(() => {
    if (isValidIntegrationId(integrationGroup)) {
      const integration = getIntegrationById(integrationGroup as IntegrationId, { intentNewLayout });
      const showNewBadge = integration && !integrationId && isIntegrationNew(integration.newUntil);
      const showSearch = integration?.hasViewScreen && integration?.hasSearch && !integrationId;
      return [integration, showNewBadge, showSearch];
    }
    return [];
  }, [integrationGroup, integrationId, intentNewLayout]);

  const memoizedContext: IntegrationsOutletContext | null = useMemo(() => {
    if (!integration?.hasViewScreen) return null;

    return {
      integration,
      setLayoutActions,
      setLayoutMeta,
      setLayoutHeading: setHeading,
      setLayoutTitle: setTitle,
      setLayoutNavigation,
    };
  }, [integration]);

  useTitle(title ? `Integrations · ${title}` : "Integrations");

  if (!integration?.hasViewScreen) {
    return <NotFoundPage />;
  }

  return (
    <>
      <ViewHeader firstLevel>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            {typeof heading === "string" ? <ViewHeaderTitle>{heading}</ViewHeaderTitle> : heading}
            {showNewBadge && <BadgeNext variant="blue" type="strong" text="New" />}
          </ViewHeaderWrapper>
          <ViewHeaderWrapper direction="row" justify="end">
            {showSearch && (
              <DesktopView>
                <SearchInput
                  filtersOrderSettingsKey="integrations"
                  placeholder={`Search ${integrationGroup}...`}
                />
              </DesktopView>
            )}
            {integration.docsLink && (
              <DocumentationIconButton
                tooltipText={`View documentation for ${integration.name}`}
                href={integration.docsLink}
                variant="secondary"
              />
            )}
            {layoutActions}
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>
        {layoutMeta && (
          <ViewHeaderScrollCollapse>
            <MetaInfoList>{layoutMeta}</MetaInfoList>
          </ViewHeaderScrollCollapse>
        )}
        {showSearch && (
          <MobileView>
            <SearchInput
              filtersOrderSettingsKey="integrations"
              placeholder={`Search ${integrationGroup}...`}
            />
          </MobileView>
        )}
        {layoutNavigation && <ViewHeaderNavigation>{layoutNavigation}</ViewHeaderNavigation>}
      </ViewHeader>
      <Outlet context={memoizedContext} />
    </>
  );
};

export default IntegrationGroupLayout;
